import { useMemo, useState } from "react";
import { CalendarClock, CalendarRange, XCircle, Flame } from "lucide-react";

import Panel from "../Panel";
import Skeleton from "../Skeleton";
import StatRow from "../StatRow";
import {
  Badge,
  Chip,
  Chips,
  Empty,
  Note,
  PageStack,
  PrimaryText,
  SecondaryText,
  Table,
  TableScroll,
  Td,
  Th,
  Toolbar,
  Tr,
} from "../ui";
import { PageProps, daysLabel, formatDate } from "./types";
import { daysLeft, memberDirectory } from "../../../utils/partnerMetrics";

const WINDOWS = [7, 14, 30];

const ExpiringPage = ({ members, loading }: PageProps) => {
  const [windowDays, setWindowDays] = useState(14);

  const upcoming = useMemo(
    () =>
      memberDirectory(members)
        .filter((m) => m.status !== "expired")
        .map((m) => ({ ...m, days: daysLeft(m.expiresAt) }))
        .filter((m) => !isNaN(m.days) && m.days >= 0 && m.days <= 30)
        .sort((a, b) => a.days - b.days),
    [members]
  );

  const within = (n: number) => upcoming.filter((m) => m.days <= n);
  const visible = within(windowDays);

  return (
    <PageStack>
      <StatRow
        loading={loading}
        stats={[
          { label: "Ending in 7 Days", value: String(within(7).length), icon: CalendarClock },
          { label: "Ending in 30 Days", value: String(upcoming.length), icon: CalendarRange },
          {
            label: "Set to Cancel",
            value: String(upcoming.filter((m) => m.status === "cancelling").length),
            icon: XCircle,
            hint: "Within 30 days",
          },
          {
            label: "Trials Ending",
            value: String(upcoming.filter((m) => m.status === "trial").length),
            icon: Flame,
            hint: "Within 30 days",
          },
        ]}
      />
      <Panel title="Memberships Ending Soon" subtitle="Current billing periods that end in the selected window">
        <Toolbar>
          <Chips>
            {WINDOWS.map((w) => (
              <Chip key={w} $active={windowDays === w} onClick={() => setWindowDays(w)}>
                Next {w} days · {within(w).length}
              </Chip>
            ))}
          </Chips>
        </Toolbar>
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} $h="36px" $mb="8px" />)
        ) : visible.length === 0 ? (
          <Empty>No memberships end in the next {windowDays} days.</Empty>
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Member</Th>
                  <Th>Plan</Th>
                  <Th>Ends</Th>
                  <Th>Time left</Th>
                  <Th>Subscription</Th>
                </tr>
              </thead>
              <tbody>
                {visible.map((m) => (
                  <Tr key={m.key}>
                    <Td>
                      <PrimaryText>
                        {m.firstName} {m.lastName}
                      </PrimaryText>
                      <SecondaryText>
                        {m.email}
                        {m.phone && m.phone !== "null" ? ` · ${m.phone}` : ""}
                      </SecondaryText>
                    </Td>
                    <Td data-label="Plan">{m.plan}</Td>
                    <Td data-label="Ends" $muted>{formatDate(m.expiresAt)}</Td>
                    <Td data-label="Time left">
                      <Badge $tone={m.days <= 3 ? "red" : m.days <= 7 ? "amber" : "gray"}>
                        {daysLabel(m.days)}
                      </Badge>
                    </Td>
                    <Td data-label="Subscription">
                      {m.status === "cancelling" ? (
                        <Badge $tone="red">Set to cancel</Badge>
                      ) : (
                        <Badge $tone="green">Continues</Badge>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
        <Note>
          "Continues" means the member's JackedRabbit subscription renews. They may still pick a
          different studio for their next month, so a personal check-in before their period ends
          is the best way to keep them.
        </Note>
      </Panel>
    </PageStack>
  );
};

export default ExpiringPage;
